import { useState, useEffect, useCallback } from "react";
import { useQuery, gql } from "@apollo/client";
import { Capacitor } from "@capacitor/core";
import { CapacitorNaverLogin } from "@team-lepisode/capacitor-naver-login";
import { createLogger } from "@ict/shared";
import { forceLogout } from "@/lib/apollo";
import { getRecord, putRecord } from "@/lib/db";

const logger = createLogger({ module: "useAuth" });

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:23998";
const AUTH_CACHE_KEY = "currentUser";
const SYNC_META_STORE = "syncMeta";

const ME_QUERY = gql`
  query Me {
    me {
      _id
      id
      name
      grade
      companyId
      companyName
      grants
    }
  }
`;

export interface User {
  _id: string;
  id: string;
  name: string;
  grade: number;
  companyId: string | null;
  companyName: string | null;
  grants: string[];
}

interface CachedAuthData {
  key: string;
  user: User;
  cachedAt: number;
}

/**
 * IndexedDB에 사용자 정보 저장
 */
async function cacheUser(user: User): Promise<void> {
  const data: CachedAuthData = {
    key: AUTH_CACHE_KEY,
    user,
    cachedAt: Date.now(),
  };
  await putRecord(SYNC_META_STORE, data);
}

/**
 * IndexedDB에서 캐시된 사용자 정보 조회
 */
async function getCachedUser(): Promise<User | null> {
  try {
    const data = await getRecord<CachedAuthData>(SYNC_META_STORE, AUTH_CACHE_KEY);
    return data?.user ?? null;
  } catch {
    return null;
  }
}

export function useAuth() {
  const [cachedUser, setCachedUser] = useState<User | null>(null);
  const [isCacheLoading, setIsCacheLoading] = useState(true);

  // IndexedDB에서 캐시된 사용자 정보 로드 (초기 로딩)
  useEffect(() => {
    getCachedUser()
      .then((user) => {
        if (user) {
          setCachedUser(user);
        }
      })
      .finally(() => {
        setIsCacheLoading(false);
      });
  }, []);

  const {
    data,
    loading: queryLoading,
    error,
    refetch,
  } = useQuery<{ me: User | null }>(ME_QUERY, {
    // cache-first: 오프라인에서도 Apollo 캐시 사용 시도
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-and-network",
    // 인증 에러를 감지하기 위해 모든 에러를 data와 함께 반환
    errorPolicy: "all",
  });

  const logout = useCallback(async () => {
    try {
      // REST API로 로그아웃 (서버 세션 종료)
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // 오프라인에서 로그아웃 시도 시 무시
    }

    // 네이버 SDK 세션 해제 (네이티브 환경에서만)
    if (Capacitor.isNativePlatform()) {
      try {
        await CapacitorNaverLogin.logout();
        logger.debug("네이버 SDK 세션 해제 완료");
      } catch (error) {
        logger.warn("네이버 SDK 로그아웃 실패 (무시 가능):", error);
      }
    }

    // forceLogout: 토큰 삭제 + Apollo 캐시 + IndexedDB 전체 정리 + 리다이렉트
    await forceLogout();
    setCachedUser(null);
  }, []);

  // 온라인에서 사용자 정보 수신 시 IndexedDB에 캐시
  useEffect(() => {
    if (data?.me) {
      cacheUser(data.me);
      setCachedUser(data.me);
    }
  }, [data?.me]);

  // 최종 사용자: Apollo 데이터 우선, 없으면 IndexedDB 캐시
  const user = data?.me ?? cachedUser;
  const isLoading = isCacheLoading || (queryLoading && !cachedUser);

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    logout,
    refetch,
  };
}
